
import React from 'react';
import { BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BotSignalsTable } from './BotSignalsTable';
import { CTraderSignalsTable } from './CTraderSignalsTable';
import { useSignalHistory } from '@/hooks/use-signal-history';
import { useTranslation } from 'react-i18next';

interface SignalHistorySectionProps {
  botId: string | undefined;
}

export const SignalHistorySection = ({ botId }: SignalHistorySectionProps) => {
  const { t } = useTranslation();

  const { 
    botSignals,
    ctraderSignals,
    botSignalsTotal,
    ctraderSignalsTotal,
    botSignalsPage,
    botSignalsPageSize,
    ctraderSignalsPage,
    ctraderSignalsPageSize,
    isBotSignalsLoading,
    isCtraderSignalsLoading,
    activeTab,
    setActiveTab,
    handleBotSignalsPageChange,
    handleBotSignalsPageSizeChange,
    handleCtraderSignalsPageChange,
    handleCtraderSignalsPageSizeChange,
    refreshSignals
  } = useSignalHistory(botId);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} />
            <CardTitle>{t("signal_history")}</CardTitle>
          </div>
        </div>
        <CardDescription>
          {t("view_signal_history")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="bot-signals">{t("bot_signals")}</TabsTrigger>
            <TabsTrigger value="ctrader-signals">{t("ctrader_signals")}</TabsTrigger>
          </TabsList>
          
          <TabsContent value="bot-signals" className="space-y-4">
            <BotSignalsTable
              signals={botSignals}
              isLoading={isBotSignalsLoading}
              onRefresh={refreshSignals}
              currentPage={botSignalsPage}
              pageSize={botSignalsPageSize}
              totalItems={botSignalsTotal}
              onPageChange={handleBotSignalsPageChange}
              onPageSizeChange={handleBotSignalsPageSizeChange}
            />
          </TabsContent>
          
          <TabsContent value="ctrader-signals" className="space-y-4">
            <CTraderSignalsTable
              signals={ctraderSignals}
              isLoading={isCtraderSignalsLoading}
              onRefresh={refreshSignals}
              currentPage={ctraderSignalsPage}
              pageSize={ctraderSignalsPageSize}
              totalItems={ctraderSignalsTotal}
              onPageChange={handleCtraderSignalsPageChange}
              onPageSizeChange={handleCtraderSignalsPageSizeChange}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
